import { ColumnDef } from "@tanstack/react-table";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar.tsx";
import { formatDate, getInitials } from "@/lib/utils.ts";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Mentorship } from "@/lib/api/mentorships.ts";
import { UpdateMentorship } from "@/components/users/user-details/mentorships/update-mentorship.tsx";

export const getColumns: (
  userId: string,
  isMentor: boolean,
  mentorships: Mentorship[],
) => ColumnDef<Mentorship>[] = (userId, isMentor, mentorships) => [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const name = isMentor
        ? row.original.mentee_name
        : row.original.mentor_name;
      const profilePictureUrl = isMentor
        ? row.original.mentee_profile_picture_url
        : row.original.mentor_profile_picture_url;

      return (
        <div className="flex max-w-md items-center gap-2">
          <Avatar className="">
            <AvatarImage src={profilePictureUrl} />
            <AvatarFallback>{getInitials(name)}</AvatarFallback>
          </Avatar>

          <div className="flex flex-col gap-1 overflow-hidden">
            <span className="text-base font-bold leading-tight text-black">
              {name}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "start_date",
    enableHiding: false,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Start Date" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-center">
          {row.original.start_date ? formatDate(row.original.start_date) : "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "days_in_mentorship",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Days in Mentorship" />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-center">
          {row.original.days_in_mentorship || "-"}
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const deactivated = Boolean(row.original.deactivated);

      if (deactivated) {
        return (
          <div className="flex items-center justify-center">
            <Badge variant="outline" className="px-4 py-2">
              Inactive
            </Badge>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center">
          <Badge
            variant="secondary"
            className="bg-black px-4 py-2 text-white hover:bg-black"
          >
            Active
          </Badge>
        </div>
      );
    },
    enableHiding: false,
    enableSorting: false,
  },
  {
    accessorKey: "actions",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Actions" />
    ),
    cell: ({ row }) => {
      const isMenteeDeactivated = row.original.mentee_deactivated;
      const isMentorDeactivated = row.original.mentor_deactivated;

      const disabled =
        isMenteeDeactivated ||
        isMentorDeactivated ||
        (!isMentor
          ? mentorships?.some((mentorship) => !mentorship.deactivated)
          : false);

      return (
        <div
          className="flex items-center justify-center"
          onClick={(event) => event.stopPropagation()}
        >
          <UpdateMentorship
            mentorshipId={row.original.mentorship_id}
            deactivated={Boolean(row.original.deactivated)}
            userId={userId}
            disabled={row.original.deactivated ? disabled : false}
          />
        </div>
      );
    },
    enableHiding: false,
    enableSorting: false,
  },
];
